import { type Recipe, RecipeCategory, RecipeType } from "src/graphql/generated/graphql";
import breakfastMeat from '../../../../../assets/placeholders/breakfast-meat-256.png';
import breakfastMeat128 from '../../../../../assets/placeholders/breakfast-meat-128.png';
import breakfastMeat64 from '../../../../../assets/placeholders/breakfast-meat-64.png';
import breakfastVeggie from '../../../../../assets/placeholders/breakfast-veggie-256.png';
import breakfastVeggie128 from '../../../../../assets/placeholders/breakfast-veggie-128.png';
import breakfastVeggie64 from '../../../../../assets/placeholders/breakfast-veggie-64.png';
import breakfastVegan from '../../../../../assets/placeholders/breakfast-vegan.png';
import breakfastVegan128 from '../../../../../assets/placeholders/breakfast-vegan-128.png';
import breakfastVegan64 from '../../../../../assets/placeholders/breakfast-vegan-64.png';
import breakfastFish from '../../../../../assets/placeholders/breakfast-fish-256.png';
import breakfastFish128 from '../../../../../assets/placeholders/breakfast-fish-128.png';
import breakfastFish64 from '../../../../../assets/placeholders/breakfast-fish-64.png';
import lunchMeat from '../../../../../assets/placeholders/lunch-meat-256.png';
import lunchMeat128 from '../../../../../assets/placeholders/lunch-meat-128.png';
import lunchMeat64 from '../../../../../assets/placeholders/lunch-meat-64.png';
import lunchVeggie from '../../../../../assets/placeholders/lunch-veggie-256.png';
import lunchVeggie128 from '../../../../../assets/placeholders/lunch-veggie-128.png';
import lunchVeggie64 from '../../../../../assets/placeholders/lunch-veggie-64.png';
import lunchVegan from '../../../../../assets/placeholders/lunch-vegan-256.png';
import lunchVegan128 from '../../../../../assets/placeholders/lunch-vegan-128.png';
import lunchVegan64 from '../../../../../assets/placeholders/lunch-vegan-64.png';
import lunchFish from '../../../../../assets/placeholders/lunch-fish-256.png';
import lunchFish128 from '../../../../../assets/placeholders/lunch-fish-128.png';
import lunchFish64 from '../../../../../assets/placeholders/lunch-fish-64.png';
import dinnerMeat from '../../../../../assets/placeholders/dinner-meat-256.png';
import dinnerMeat128 from '../../../../../assets/placeholders/dinner-meat-128.png';
import dinnerMeat64 from '../../../../../assets/placeholders/dinner-meat-64.png';
import dinnerVeggie from '../../../../../assets/placeholders/dinner-veggie-256.png';
import dinnerVeggie128 from '../../../../../assets/placeholders/dinner-veggie-128.png';
import dinnerVeggie64 from '../../../../../assets/placeholders/dinner-veggie-64.png';
import dinnerVegan from '../../../../../assets/placeholders/dinner-vegan-256.png';
import dinnerVegan128 from '../../../../../assets/placeholders/dinner-vegan-128.png';
import dinnerVegan64 from '../../../../../assets/placeholders/dinner-vegan-64.png';
import dinnerFish from '../../../../../assets/placeholders/dinner-fish-256.png';
import dinnerFish128 from '../../../../../assets/placeholders/dinner-fish-128.png';
import dinnerFish64 from '../../../../../assets/placeholders/dinner-fish-64.png';
import { normalizeRecipeCategory } from 'src/graphql/utils';

interface RecipeImageProps {
    recipe: Recipe;
    thumbnail?: number | boolean;
    variant?: "cover" | "hero";
}

const DEFAULT_SIZES_SMALL = "(max-width: 480px) 64px, (max-width: 1024px) 128px, 256px";
const DEFAULT_SIZES_FLEX = "(max-width: 600px) 100vw, 50vw";

function getDefaultImage(recipeType: RecipeType, recipeCategory: RecipeCategory) {
    switch (normalizeRecipeCategory(recipeCategory)) {
        case RecipeCategory.Breakfast:
            switch (recipeType) {
                case RecipeType.Meat:
                    return breakfastMeat;
                case RecipeType.Veggie:
                    return breakfastVeggie;
                case RecipeType.Vegan:
                    return breakfastVegan;
                case RecipeType.Fish:
                    return breakfastFish;
                default:
                    return null;
            }
        case RecipeCategory.Lunch:
            switch (recipeType) {
                case RecipeType.Meat:
                    return lunchMeat;
                case RecipeType.Veggie:
                    return lunchVeggie;
                case RecipeType.Vegan:
                    return lunchVegan;
                case RecipeType.Fish:
                    return lunchFish;
                default:
                    return null;
            }
        case RecipeCategory.Dinner:
            switch (recipeType) {
                case RecipeType.Meat:
                    return dinnerMeat;
                case RecipeType.Veggie:
                    return dinnerVeggie;
                case RecipeType.Vegan:
                    return dinnerVegan;
                case RecipeType.Fish:
                    return dinnerFish;
                default:
                    return null;
            }
        default:
            return null;
    }
}
function getDefaultSrcSet(recipeType: RecipeType, recipeCategory: RecipeCategory): string | undefined {
    switch (normalizeRecipeCategory(recipeCategory)) {
        case RecipeCategory.Breakfast:
            switch (recipeType) {
                case RecipeType.Meat:
                    return `${breakfastMeat64} 64w, ${breakfastMeat128} 128w, ${breakfastMeat} 256w`;
                case RecipeType.Veggie:
                    return `${breakfastVeggie64} 64w, ${breakfastVeggie128} 128w, ${breakfastVeggie} 256w`;
                case RecipeType.Vegan:
                    return `${breakfastVegan64} 64w, ${breakfastVegan128} 128w, ${breakfastVegan} 256w`;
                case RecipeType.Fish:
                    return `${breakfastFish64} 64w, ${breakfastFish128} 128w, ${breakfastFish} 256w`;
                default:
                    return undefined;
            }
        case RecipeCategory.Lunch:
            switch (recipeType) {
                case RecipeType.Meat:
                    return `${lunchMeat64} 64w, ${lunchMeat128} 128w, ${lunchMeat} 256w`;
                case RecipeType.Veggie:
                    return `${lunchVeggie64} 64w, ${lunchVeggie128} 128w, ${lunchVeggie} 256w`;
                case RecipeType.Vegan:
                    return `${lunchVegan64} 64w, ${lunchVegan128} 128w, ${lunchVegan} 256w`;
                case RecipeType.Fish:
                    return `${lunchFish64} 64w, ${lunchFish128} 128w, ${lunchFish} 256w`;
                default:
                    return undefined;
            }
        case RecipeCategory.Dinner:
            switch (recipeType) {
                case RecipeType.Meat:
                    return `${dinnerMeat64} 64w, ${dinnerMeat128} 128w, ${dinnerMeat} 256w`;
                case RecipeType.Veggie:
                    return `${dinnerVeggie64} 64w, ${dinnerVeggie128} 128w, ${dinnerVeggie} 256w`;
                case RecipeType.Vegan:
                    return `${dinnerVegan64} 64w, ${dinnerVegan128} 128w, ${dinnerVegan} 256w`;
                case RecipeType.Fish:
                    return `${dinnerFish64} 64w, ${dinnerFish128} 128w, ${dinnerFish} 256w`;
                default:
                    return undefined;
            }
        default:
            return undefined;
    }
}

export default function RecipeImage({ recipe, thumbnail }: RecipeImageProps) {
    let imgSrc: string | null | undefined;
    let srcSet: string | undefined;
    let sizes: string | undefined;
    let isDefault = false;
    if (!recipe) {
        return null;
    }
    if (!recipe.imageUrl) {
        isDefault = true;
        imgSrc = getDefaultImage(recipe.type, recipe.category);
        srcSet = getDefaultSrcSet(recipe.type, recipe.category);
        sizes = DEFAULT_SIZES_SMALL;
    } else {
        imgSrc = recipe.imageUrl;
    }
    // Determine rendered size: number -> that size, true -> small default, falsy -> full default
    const size = typeof thumbnail === 'number' ? thumbnail : (thumbnail ? 64 : 256);
    return (
        <>
            {imgSrc ? (
                <img
                    src={imgSrc}
                    srcSet={srcSet}
                    sizes={sizes}
                    loading="lazy"
                    decoding="async"
                    alt={recipe.name && recipe.name !== ''? recipe.name : 'Recipe Image'}
                    width={size}
                    height={size}
                    style={{ borderRadius: thumbnail ? undefined : 12, margin: 0, padding: 0, opacity: isDefault ? 0.5 : 1 }}
                />
            ) : null}
        </>
    );
}

export function RecipeImageFlex({ recipe, variant = "cover" }: RecipeImageProps) {
    let imgSrc: string | null | undefined;
    let srcSet: string | undefined;
    let sizes: string | undefined;
    let isDefault = false;
    if (!recipe) {
      return null;
    }
    if (!recipe.imageUrl) {
      isDefault = true;
      imgSrc = getDefaultImage(recipe.type, recipe.category);
      srcSet = getDefaultSrcSet(recipe.type, recipe.category);
      sizes = DEFAULT_SIZES_FLEX;
    } else {
      imgSrc = recipe.imageUrl;
    }
    if (!imgSrc) {
      return null;
    }

    if (variant === "hero") {
      return (
        <div data-testid="recipe-image-hero" style={{ position: "relative", zIndex: 0, width: "100%", height: "100%", overflow: "hidden", background: "#f1f5e8" }}>
          <img
            data-testid="recipe-image-hero-background"
            src={imgSrc}
            aria-hidden="true"
            alt=""
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              filter: isDefault ? "blur(8px)" : "blur(22px)",
              transform: isDefault ? "scale(1.03)" : "scale(1.12)",
              opacity: isDefault ? 0.24 : 0.64,
            }}
          />
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              inset: 0,
              background: isDefault ? "rgba(0, 0, 0, 0.04)" : "linear-gradient(180deg, rgba(0, 0, 0, 0.10), rgba(0, 0, 0, 0.20))",
            }}
          />
          <img
            data-testid="recipe-image-hero-foreground"
            src={imgSrc}
            srcSet={srcSet}
            sizes={sizes}
            loading="lazy"
            decoding="async"
            alt={recipe.name && recipe.name !== '' ? recipe.name : 'Recipe Image'}
            style={{
              position: "relative",
              zIndex: 1,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              margin: 0,
              padding: 0,
              opacity: isDefault ? 0.72 : 1,
            }}
          />
        </div>
      );
    }

    return (
      <img
        data-testid="recipe-image-flex"
        src={imgSrc}
        srcSet={srcSet}
        sizes={sizes}
        loading="lazy"
        decoding="async"
        alt={recipe.name && recipe.name !== '' ? recipe.name : 'Recipe Image'}
        style={{ width: '100%', height: '100%', objectFit: 'cover', margin: 0, padding: 0, opacity: isDefault ? 0.5 : 1 }}
      />
    );
  }
